import React, { useState } from 'react';
import { Element, scroller } from 'react-scroll';

import styles from './userSavedSearchesPageStyles.scss';

import DeletedSavedFundSearchesContainer from '@buyside/components/container/UserAccount/DeletedSavedFundSearchesContainer';
import DeletedSavedIdeaSearchesContainer from '@buyside/components/container/UserAccount/DeletedSavedIdeaSearchesContainer';
import Link from '@buyside/components/presentation/Link/link';
import SavedSearchAlertIdeaContainer from '@buyside/components/container/UserAccount/SavedSearchAlertIdeaContainer';
import SavedSearchAlertFundContainer from '@buyside/components/container/UserAccount/SavedSearchAlertFundContainer';

import {
  EmployerProfileSearchAlert,
  useCurrentUserAccountSavedSearchesQuery
} from '@interfaces/graphql-typings';

import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';

import { ideasUrl, emailSettingUrl } from '@buyside/util/urlHelper';

type DeleteObjType = { [key: string]: string };

const UserSavedSearchesPage = (): React.ReactElement => {
  const { loading, data } = useCurrentUserAccountSavedSearchesQuery();

  const [deletedIdeasObj, setDeletedIdeasObj] = useState({});
  const [deletedFundsObj, setDeletedFundsObj] = useState({});

  if (loading || !data?.currentUser) return <LoadingSpinner size={SIZES.LARGE} />;

  const { ideaSearchAlerts, fundProfileSearchAlerts } = data.currentUser;

  const pageScrollTo = (location: string): void => {
    scroller.scrollTo(location, {
      duration: 1000,
      delay: 100,
      smooth: true,
      offset: -(65 + 15) // -(top navigation height + some padding)
    });
  };

  const addIdeaAlertToSoftDelete = (alert: EmployerProfileSearchAlert): void => {
    const deletedIdeas = { ...deletedIdeasObj, [alert.id]: alert };
    setDeletedIdeasObj(deletedIdeas);
    pageScrollTo('ideaTop');
  };

  const addFundAlertToSoftDelete = (alert: EmployerProfileSearchAlert): void => {
    const deletedFunds = { ...deletedFundsObj, [alert.id]: alert };
    setDeletedFundsObj(deletedFunds);
    pageScrollTo('fundTop');
  };

  const deletedIdeas = Object.keys(deletedIdeasObj);
  const deletedFunds = Object.keys(deletedFundsObj);

  return (
    <>
      <div className={styles.pageTitle}>Your Saved Searches</div>
      <h4 className={styles.pageSubsectionTitle}>Add Saved Searches</h4>
      <p className={styles.pageDescription}>
        When you search for research on SumZero and elect to save your search criteria, any saved
        searches will appear in this area. Try{' '}
        <Link to={ideasUrl()} className={styles.highlightLink}>
          searching for research
        </Link>{' '}
        and selecting "Save Search" to get started.
      </p>
      <Element name="ideaTop">
        <h4 className={styles.pageSubsectionTitle}>Ideas</h4>
      </Element>
      {ideaSearchAlerts && ideaSearchAlerts.length > 0 && (
        <div className={styles.ideaAlertFrequency}>
          You are currently receiving <strong>{ideaSearchAlerts[0]?.frequency}</strong> emails.
          Change this schedule in <Link to={emailSettingUrl()}>your email settings</Link>.
        </div>
      )}
      {deletedIdeas.length ? (
        <DeletedSavedIdeaSearchesContainer
          deletedSearches={deletedIdeasObj}
          removeFromSoftDelete={(deletedSearches: DeleteObjType): void =>
            setDeletedIdeasObj(deletedSearches)
          }
        />
      ) : null}
      {ideaSearchAlerts?.length ? (
        <div>
          {ideaSearchAlerts.map(alert => (
            <SavedSearchAlertIdeaContainer
              key={alert?.id}
              alert={alert}
              addToParentsSoftDelete={addIdeaAlertToSoftDelete}
            />
          ))}
        </div>
      ) : (
        <div className={styles.noContentMsg}>
          <p>You have no saved searches.</p>
        </div>
      )}
      <Element name="fundTop">
        <h4 className={styles.pageSubsectionTitle}>Fund Profiles</h4>
      </Element>
      {deletedFunds.length ? (
        <DeletedSavedFundSearchesContainer
          deletedSearches={deletedFundsObj}
          removeFromSoftDelete={(deletedSearches: DeleteObjType): void =>
            setDeletedFundsObj(deletedSearches)
          }
        />
      ) : null}
      {fundProfileSearchAlerts?.length ? (
        <div>
          {fundProfileSearchAlerts.map(alert => (
            <SavedSearchAlertFundContainer
              key={alert?.id}
              alert={alert}
              addToParentsSoftDelete={addFundAlertToSoftDelete}
            />
          ))}
        </div>
      ) : (
        <div className={styles.noContentMsg}>
          <p>You have no saved searches.</p>
        </div>
      )}
    </>
  );
};

export default UserSavedSearchesPage;
